import React from 'react';
import { BrowserRouter as Router, Routes, Route, useLocation } from 'react-router-dom';
import { TransitionGroup, CSSTransition } from 'react-transition-group';
import Navigation from './components/Navigation';
import Home from './pages/Home';
import Login from './pages/Login';
import FieldMappings from './pages/FieldMappings';
import LenderMatch from './pages/LenderMatch';
import LenderSettings from './pages/LenderSettings';
import ContactSearch from './pages/ContactSearch';
import ContactDetail from './pages/ContactDetail';
import BankParsingFieldMappings from './pages/BankParsing/FieldMappings';
import BankParsingAnalyze from './pages/BankParsing/Analyze';
import BankParsingReport from './pages/BankParsing/Report';
import BankParsingCompetitors from './pages/BankParsing/Competitors';
import { AuthProvider } from './context/AuthContext';
import { ValidationProvider } from './context/ValidationContext';
import { NotificationProvider } from './context/NotificationContext';
import ToastContainer from './components/notifications/ToastContainer';
import { ProtectedRoute, AdminRoute, SalesRoute, RestrictedFromSalesRoute } from './components/auth';
import 'bootstrap/dist/css/bootstrap.min.css';
import './App.css';
import './styles/transitions.css';
import './styles/validation.css';
import './styles/Notifications.css';

/**
 * AnimatedRoutes component to handle page transitions
 * Uses React Router and CSSTransition for smooth page navigation
 * @returns {JSX.Element} The animated routes component
 */
const AnimatedRoutes = () => {
  const location = useLocation();
  
  return (
    <>
      <TransitionGroup>
        <CSSTransition
          key={location.pathname}
          timeout={400}
          classNames="page-transition"
          unmountOnExit
        >
          <div className="page-container">
            <Routes location={location}>
              <Route path="/login" element={<Login />} />
              <Route path="/" element={
                <RestrictedFromSalesRoute>
                  <ContactSearch />
                </RestrictedFromSalesRoute>
              } />
              <Route path="/contact/:contactId" element={
                <RestrictedFromSalesRoute>
                  <ContactDetail />
                </RestrictedFromSalesRoute>
              } />
              <Route path="/field-mappings" element={
                <AdminRoute>
                  <FieldMappings />
                </AdminRoute>
              } />
              <Route path="/lender-match" element={
                <AdminRoute>
                  <LenderMatch />
                </AdminRoute>
              } />
              <Route path="/lender-settings" element={
                <AdminRoute>
                  <LenderSettings />
                </AdminRoute>
              } />
              <Route path="/bank-parsing/field-mappings" element={
                <AdminRoute>
                  <BankParsingFieldMappings />
                </AdminRoute>
              } />
              <Route path="/bank-parsing/analyze" element={
                <SalesRoute>
                  <BankParsingAnalyze />
                </SalesRoute>
              } />
              <Route path="/bank-parsing/competitors" element={
                <AdminRoute>
                  <BankParsingCompetitors />
                </AdminRoute>
              } />
              <Route path="/reports/bank-parsing" element={<BankParsingReport />} />
            </Routes>
          </div>
        </CSSTransition>
      </TransitionGroup>
    </>
  );
};

/**
 * Main App component that sets up routing and context providers
 * @returns {JSX.Element} The main application
 */
function App() {
  return (
    <Router>
      <AuthProvider>
        <ValidationProvider>
          <NotificationProvider>
            <Navigation />
            <ToastContainer />
            <AnimatedRoutes />
          </NotificationProvider>
        </ValidationProvider>
      </AuthProvider>
    </Router>
  );
}

export default App;
